/**
 * Emoticons forums : stockés en codes smiley (compat Phorum / texte FFT),
 * affichés en emoji Unicode côté Neo.
 *
 * Set volontairement limité à l’intersection chat ↔ smileys Phorum détente.
 */

export interface ForumEmoticon {
  text: string;
  emoji: string;
  description: string;
}

/** Codes canoniques insérés par le picker (et utilisés à l’envoi emoji→smiley). */
export const FORUM_EMOTICONS: ForumEmoticon[] = [
  { text: ':)', emoji: '😊', description: 'Sourire' },
  { text: ':-D', emoji: '😃', description: 'Grand sourire' },
  { text: ';-)', emoji: '😉', description: 'Clin d\'œil' },
  { text: ':(', emoji: '😞', description: 'Triste' },
  { text: ':-P', emoji: '😛', description: 'Tire la langue' },
  { text: ':-|', emoji: '😐', description: 'Neutre' },
  { text: ':-/', emoji: '😕', description: 'Dubitatif' },
  { text: ':-O', emoji: '😲', description: 'Surpris' },
];

/**
 * Alias texte → emoji (affichage Neo, y compris variantes legacy).
 * Lookbehind anti alphanum pour ne pas casser `https://` ni coller dans les mots.
 */
const SMILEY_TO_EMOJI: Array<{ pattern: RegExp; emoji: string }> = [
  { pattern: /(?<![A-Za-z0-9]):-\)/g, emoji: '😊' },
  { pattern: /(?<![A-Za-z0-9]):\)/g, emoji: '😊' },
  { pattern: /(?<![A-Za-z0-9])=\)/g, emoji: '😊' },
  { pattern: /(?<![A-Za-z0-9]):o\)/gi, emoji: '😊' },
  { pattern: /(?<![A-Za-z0-9]):-D/g, emoji: '😃' },
  { pattern: /(?<![A-Za-z0-9]):D/g, emoji: '😃' },
  { pattern: /(?<![A-Za-z0-9])=D/g, emoji: '😃' },
  { pattern: /(?<![A-Za-z0-9]);-\)/g, emoji: '😉' },
  { pattern: /(?<![A-Za-z0-9]);\)/g, emoji: '😉' },
  { pattern: /(?<![A-Za-z0-9]):-\(/g, emoji: '😞' },
  { pattern: /(?<![A-Za-z0-9]):\(/g, emoji: '😞' },
  { pattern: /(?<![A-Za-z0-9])=\(/g, emoji: '😞' },
  { pattern: /(?<![A-Za-z0-9]):-P/g, emoji: '😛' },
  { pattern: /(?<![A-Za-z0-9]):P/g, emoji: '😛' },
  { pattern: /(?<![A-Za-z0-9]):-p/g, emoji: '😛' },
  { pattern: /(?<![A-Za-z0-9]):p/g, emoji: '😛' },
  { pattern: /(?<![A-Za-z0-9]):oP/gi, emoji: '😛' },
  { pattern: /(?<![A-Za-z0-9]):-\|/g, emoji: '😐' },
  { pattern: /(?<![A-Za-z0-9]):\|/g, emoji: '😐' },
  { pattern: /(?<![A-Za-z0-9]):-\//g, emoji: '😕' },
  { pattern: /(?<![A-Za-z0-9]):\//g, emoji: '😕' },
  { pattern: /(?<![A-Za-z0-9]):-O/g, emoji: '😲' },
  { pattern: /(?<![A-Za-z0-9]):O/g, emoji: '😲' },
  { pattern: /(?<![A-Za-z0-9]):-o/g, emoji: '😮' },
];

/** Convertit les smileys texte en emoji (affichage). Patterns du plus long au plus court. */
export function convertEmoticonsToEmojis(text: string): string {
  if (!text) return text;
  let result = text;
  for (const { pattern, emoji } of SMILEY_TO_EMOJI) {
    result = result.replace(pattern, emoji);
  }
  return result;
}

/** Convertit les emoji du set forum en codes smiley canoniques (avant POST). */
export function convertForumEmojisToSmileys(text: string): string {
  if (!text) return text;
  let result = text;
  for (const { emoji, text: smiley } of FORUM_EMOTICONS) {
    result = result.split(emoji).join(smiley);
  }
  return result;
}

/**
 * Applique convertEmoticonsToEmojis uniquement hors balises HTML
 * (évite de toucher aux attributs / URLs dans les tags).
 */
export function convertEmoticonsToEmojisOutsideTags(html: string): string {
  if (!html) return html;
  return html
    .split(/(<[^>]+>)/g)
    .map((part) => (part.startsWith('<') ? part : convertEmoticonsToEmojis(part)))
    .join('');
}

/** Insère un code smiley à la position du curseur d’un textarea contrôlé. */
export function insertAtCursor(
  value: string,
  insert: string,
  textarea: HTMLTextAreaElement | null,
): { next: string; caret: number } {
  if (!textarea) {
    return { next: value + insert, caret: value.length + insert.length };
  }
  const start = textarea.selectionStart ?? value.length;
  const end = textarea.selectionEnd ?? value.length;
  const next = value.slice(0, start) + insert + value.slice(end);
  return { next, caret: start + insert.length };
}
